import * as React from 'react';
import {StyleSheet, View, ViewStyle} from 'react-native';

import BaseSceneCommandModal from './BaseSceneCommandModal';

import CharacterViewWithEditor from './visualizations/CharacterViewWithEditor';
import CharacterViewWithEditorForCharacterMaker from './visualizations/CharacterViewWithEditorForCharacterMaker';

import CharacterExitButton from './buttons/CharacterExitButton';
import ChangeButton from './buttons/ChangeButton';

import CharacterPatternMapping from '../../../../../view_models/CharacterPatternMapping';

import {Params as ActorCharacterFaceIndexParams} from '../../../../../actions/actor_character_faces/index';
import {Params as CharacterPatternCreateParams} from '../../../../../actions/character_patterns/create';
import {Params as ActorCostumeIndexParams} from '../../../../../actions/actor_costumes/index';
import {Params as ActorHairStyleIndexParams} from '../../../../../actions/actor_hair_styles/index';
import {Params as ActorAccessorySetIndexParams} from '../../../../../actions/actor_accessory_sets/index';

import {QueryState} from '../../../../../reducers/queries/Response';

import {CharacterSceneCommandForm} from '../../../../../view_models/SceneFrame';
import CompositeSequenceSceneCommandFormFactory from '../../../../../view_models/CompositeSequenceSceneCommandFormFactory';

import ActorCharacterFace from '../../../../../../domain/entities/ActorCharacterFace';
import CharacterPattern from '../../../../../../domain/entities/CharacterPattern';
import ActorCostume from '../../../../../../domain/entities/ActorCostume';
import ActorHairStyle from '../../../../../../domain/entities/ActorHairStyle';
import ActorAccessorySet from '../../../../../../domain/entities/ActorAccessorySet';
import Mark from '../../../../../../domain/entities/Mark';
import PaginatedResult from '../../../../../../domain/results/PaginatedResult';
import SceneCommandForm from '../../../../../../domain/forms/scene_commands/SceneCommandForm';
import CharacterHideSceneCommandForm from '../../../../../../domain/forms/scene_commands/CharacterHideSceneCommandForm';
import CharacterUpdateSceneCommandForm from '../../../../../../domain/forms/scene_commands/CharacterUpdateSceneCommandForm';
import Position from '../../../../../../domain/value_objects/Position';

interface Props {
  sceneCommandForm: CharacterSceneCommandForm;
  sceneCommandIndex: number;
  actorCharacterFaceEntities: {[key: number]: ActorCharacterFace};
  actorCharacterFaceQueries: QueryState;
  marks: Mark[] | null;
  parentSceneCommandForm?: SceneCommandForm | null;
  onRequestCloseModal: () => void;
  onForwardToCharacters: (params: {
    sceneCommandIndex?: number;
    parentSceneCommandId?: number;
    position?: Position;
  }) => void;
  onInsertCharacterUpdateCommandAt: (
    sceneCommandForm: CharacterSceneCommandForm,
    sceneCommandIndex: number,
    characterPatternMapping?: CharacterPatternMapping,
  ) => void;
  onInsertCharacterHideCommandAt: (
    sceneCommandForm: CharacterSceneCommandForm,
    sceneCommandIndex: number,
  ) => void;
  onRequestIndexActorCharacterFaces: (
    params: ActorCharacterFaceIndexParams,
  ) => Promise<PaginatedResult<ActorCharacterFace>>;
  onRequestIndexActorCostumes: (
    params: ActorCostumeIndexParams,
  ) => Promise<PaginatedResult<ActorCostume>>;
  onRequestIndexActorHairStyles: (
    params: ActorHairStyleIndexParams,
  ) => Promise<PaginatedResult<ActorHairStyle>>;
  onRequestIndexActorAccessorySets: (
    params: ActorAccessorySetIndexParams,
  ) => Promise<PaginatedResult<ActorAccessorySet>>;
  onRequestIndexMarks: () => Promise<PaginatedResult<Mark>>;
  onRequestCreateCharacterPattern: (
    params: CharacterPatternCreateParams,
  ) => Promise<CharacterPattern>;
  onChangeCommand: (
    sceneCommandForm: SceneCommandForm,
    characterPatternMapping?: CharacterPatternMapping,
  ) => void;
  onRemoveCommand: (sceneCommandForm: SceneCommandForm) => void;
}

interface State {
  loading?: boolean;
}

export default class CurrentSceneFrameModal extends React.PureComponent<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  public render(): React.ReactNode {
    const {
      sceneCommandForm,
      actorCharacterFaceEntities,
      actorCharacterFaceQueries,
      marks,
      onRequestIndexActorCharacterFaces,
      onRequestIndexActorCostumes,
      onRequestIndexActorHairStyles,
      onRequestIndexActorAccessorySets,
      onRequestIndexMarks,
      onRequestCloseModal,
    } = this.props;
    const {loading} = this.state;
    return (
      <BaseSceneCommandModal
        title={'キャラクターの編集'}
        onRequestCloseModal={onRequestCloseModal}
        loading={loading}
        footer={
          <View style={styles.footer}>
            <CharacterExitButton onPress={this.addCharacterHideSceneCommand} />
          </View>
        }>
        <View style={styles.container}>
          {sceneCommandForm.characterPattern.character.characterMakerOnly ? (
            <CharacterViewWithEditorForCharacterMaker
              sceneCommandForm={sceneCommandForm}
              actorCharacterFaceEntities={actorCharacterFaceEntities}
              actorCharacterFaceQueries={actorCharacterFaceQueries}
              marks={marks}
              inverted={sceneCommandForm.characterPattern.character.inverted}
              onRequestIndexActorCharacterFaces={
                onRequestIndexActorCharacterFaces
              }
              onRequestIndexMarks={onRequestIndexMarks}
              onSubmit={this.handleSubmit}
              onRequestLoading={this.handleRequestLoading}
            />
          ) : (
            <CharacterViewWithEditor
              sceneCommandForm={sceneCommandForm}
              actorCharacterFaceEntities={actorCharacterFaceEntities}
              actorCharacterFaceQueries={actorCharacterFaceQueries}
              marks={marks}
              inverted={sceneCommandForm.characterPattern.character.inverted}
              onRequestIndexActorCharacterFaces={
                onRequestIndexActorCharacterFaces
              }
              onRequestIndexActorCostumes={onRequestIndexActorCostumes}
              onRequestIndexActorHairStyles={onRequestIndexActorHairStyles}
              onRequestIndexActorAccessorySets={
                onRequestIndexActorAccessorySets
              }
              onRequestIndexMarks={onRequestIndexMarks}
              onSubmit={this.handleSubmit}
            />
          )}
          <View style={styles.characterChangeButton}>
            <ChangeButton
              title={'キャラ変更'}
              onPress={this.handleForwardToCharacters}
            />
          </View>
        </View>
      </BaseSceneCommandModal>
    );
  }

  private handleForwardToCharacters = () => {
    const {
      sceneCommandForm,
      sceneCommandIndex,
      parentSceneCommandForm,
      onForwardToCharacters,
      onRequestCloseModal,
    } = this.props;
    const parentSceneCommandId = parentSceneCommandForm?.sceneCommandId;
    onForwardToCharacters({
      sceneCommandIndex,
      parentSceneCommandId,
      position: sceneCommandForm.position,
    });
    onRequestCloseModal();
  };

  private addCharacterHideSceneCommand = () => {
    const {
      sceneCommandForm,
      sceneCommandIndex,
      parentSceneCommandForm,
      onRequestCloseModal,
      onInsertCharacterHideCommandAt,
      onChangeCommand,
    } = this.props;
    if (parentSceneCommandForm) {
      const newParentSceneCommandForm =
        CompositeSequenceSceneCommandFormFactory.add(
          parentSceneCommandForm,
          new CharacterHideSceneCommandForm(
            sceneCommandForm.characterPattern,
            sceneCommandForm.actorCharacterFace,
            sceneCommandForm.mark,
            sceneCommandForm.position,
          ),
        );
      onChangeCommand(newParentSceneCommandForm);
    } else {
      onInsertCharacterHideCommandAt(sceneCommandForm, sceneCommandIndex);
    }
    onRequestCloseModal();
  };

  private handleSubmit = async (
    selectedActorCharacterFace: ActorCharacterFace,
    selectedMark: Mark | null,
  ) => {
    const {
      sceneCommandForm,
      sceneCommandIndex,
      parentSceneCommandForm,
      onRequestCloseModal,
      onRequestCreateCharacterPattern,
      onInsertCharacterUpdateCommandAt,
      onChangeCommand,
    } = this.props;

    const characterPattern =
      sceneCommandForm.characterPattern.actorCharacterId ===
      selectedActorCharacterFace.actorCharacterId
        ? sceneCommandForm.characterPattern
        : await onRequestCreateCharacterPattern({
            characterId: sceneCommandForm.characterPattern.character.id,
            actorCharacterId: selectedActorCharacterFace.actorCharacterId,
          });
    let characterPatternMapping: CharacterPatternMapping | undefined =
      undefined;
    if (sceneCommandForm.characterPattern.id !== characterPattern.id) {
      characterPatternMapping = new CharacterPatternMapping();
      await characterPatternMapping.register(
        sceneCommandForm.characterPattern,
        characterPattern,
        sceneCommandForm.position,
      );
    }
    const newSceneCommandForm = new CharacterUpdateSceneCommandForm(
      characterPattern,
      selectedActorCharacterFace,
      selectedMark,
      sceneCommandForm.position,
    );
    if (parentSceneCommandForm) {
      const newParentSceneCommandForm =
        CompositeSequenceSceneCommandFormFactory.add(
          parentSceneCommandForm,
          newSceneCommandForm,
        );
      onChangeCommand(newParentSceneCommandForm, characterPatternMapping);
    } else {
      onInsertCharacterUpdateCommandAt(
        newSceneCommandForm,
        sceneCommandIndex,
        characterPatternMapping,
      );
    }
    onRequestCloseModal();
  };

  private handleRequestLoading = (loading: boolean) => {
    this.setState({loading});
  };
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: 368,
  } as ViewStyle,
  body: {
    marginBottom: 15,
  } as ViewStyle,
  button: {
    marginVertical: 5,
  } as ViewStyle,
  deleteButtons: {
    marginTop: 5,
  } as ViewStyle,
  footer: {
    padding: 16,
  } as ViewStyle,
  characterChangeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
  } as ViewStyle,
});
